import {
  CssBaseline,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  styled,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { forgotPassword } from '../services/auth.service';
import { ErrorAlert } from '../components/common/ErrorAlert';
import { SuccessAlert } from '../components/common/SuccessAlert';

const StyledPageContainer = styled(Box)({
  minHeight: '100vh',
  width: '100vw',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});

const StyledForgotCard = styled(Paper)({
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  borderRadius: '24px',
  boxShadow: '0 20px 40px rgba(102, 126, 234, 0.15), 0 10px 20px rgba(102, 126, 234, 0.1)',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '440px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
});

const StyledHeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '32px',
  gap: '12px',
});

const StyledTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '28px',
  background: 'linear-gradient(145deg, #667eea, #764ba2)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textAlign: 'center',
  marginBottom: '8px',
});

const StyledSubtitle = styled(Typography)({
  fontWeight: 500,
  color: 'rgba(0, 0, 0, 0.7)',
  textAlign: 'center',
  lineHeight: '1.5',
});

const StyledTextField = styled(TextField)({
  marginBottom: '24px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    '&:hover fieldset': {
      borderColor: '#667eea',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#667eea',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#667eea',
  },
});

const StyledButton = styled(Button)({
  borderRadius: '12px',
  padding: '14px 28px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(145deg, #667eea, #764ba2)',
  boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
  marginBottom: '24px',
  '&:hover': {
    background: 'linear-gradient(145deg, #5a6fd8, #6a42a0)',
    boxShadow: '0 12px 24px rgba(102, 126, 234, 0.4)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
});

const StyledCenterBox = styled(Box)({
  textAlign: 'center',
});

const StyledTextButton = styled(Button)({
  textTransform: 'none',
  color: '#667eea',
  fontWeight: 500,
  borderRadius: '8px',
  padding: '8px 16px',
  '&:hover': {
    background: 'rgba(102, 126, 234, 0.1)',
  },
});

export default function ForgotPassword(): JSX.Element {
  const [email, setEmail] = useState<string>('');
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const emailValue = String(formData.get('email')).trim();

    if (!emailValue) {
      setError('Please enter your email address.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await forgotPassword(emailValue);
      if (response instanceof Error) {
        setError(response.message);
      } else {
        setEmail('');
        setIsEmailSent(true);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value);
    if (error) setError(null);
  };

  const handleBackToLogin = (): void => {
    navigate('/login');
  };

  return (
    <>
      <CssBaseline />
      <StyledPageContainer>
        <StyledForgotCard elevation={0}>
          <StyledHeaderContainer>
            <StyledTitle>
              Forgot Password?
            </StyledTitle>
            <StyledSubtitle>
              No worries! Enter your email address and we'll send you a link to reset your password.
            </StyledSubtitle>
          </StyledHeaderContainer>

          {!isEmailSent ? (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {error && (
                <ErrorAlert
                  severity="error"
                  title="Reset Link Failed"
                  message={error}
                />
              )}
              <StyledTextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                value={email}
                onChange={handleEmailChange}
                autoComplete="email"
                autoFocus
                variant="outlined"
                placeholder="Enter your email address"
                disabled={loading}
              />
              <StyledButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !email.trim()}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </StyledButton>
              <StyledCenterBox>
                <StyledTextButton onClick={handleBackToLogin} disabled={loading}>
                  Back to Login
                </StyledTextButton>
              </StyledCenterBox>
            </Box>
          ) : (
            <>
              <SuccessAlert
                title="Email Sent Successfully!"
                message="Please check your email for password reset instructions. If you don't see it, check your spam folder."
              />
              <StyledCenterBox>
                <StyledTextButton onClick={handleBackToLogin}>
                  Back to Login
                </StyledTextButton>
              </StyledCenterBox>
            </>
          )}
        </StyledForgotCard>
      </StyledPageContainer>
    </>
  );
}
